import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { api, getToken, setToken, UNAUTHORIZED_EVENT } from './api.js';
import { useI18n } from './i18n/index.jsx';

const AuthContext = createContext(null);

// Ends the member session on the server, then restores the admin's token.
export async function stopImpersonation(reason = 'returned') {
  try { await api('/impersonation/end', { method: 'POST', body: { reason } }); } catch { /* already ended */ }
  restoreAdmin(null);
}

// "Log in as member" (super admins): the admin's own token waits here while
// the impersonation token is in use, and comes back on "Return to admin",
// on log out, or when the session ends (30 min idle).
const ADMIN_TOKEN_KEY = 'pme_admin_token';
const ADMIN_RETURN_KEY = 'pme_admin_return';
const store = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { if (v) localStorage.setItem(k, v); else localStorage.removeItem(k); } catch { /* private mode */ } },
};
// Back to the admin session (full reload: no member data stays in memory).
function restoreAdmin(notice) {
  const adminToken = store.get(ADMIN_TOKEN_KEY);
  const back = store.get(ADMIN_RETURN_KEY) || '/admin';
  store.set(ADMIN_TOKEN_KEY, null); store.set(ADMIN_RETURN_KEY, null);
  if (!adminToken) return false;
  setToken(adminToken);
  window.location.assign(`${back}${notice ? `${back.includes('?') ? '&' : '?'}impersonation=${notice}` : ''}`);
  return true;
}

export function AuthProvider({ children }) {
  const { lang, langSource, setLang } = useI18n();
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(!getToken());
  const [retrying, setRetrying] = useState(false);

  // Only a 401 (login really rejected) ends the session — api() then clears
  // the token. A network error, timeout, interrupted request or server
  // restart (5xx during a deploy) keeps the login and tries again.
  useEffect(() => {
    if (!getToken()) return;
    let stop = false; let timer = null; let tries = 0;
    const load = () => {
      clearTimeout(timer);
      api('/auth/me', { timeout: 15000 })
        .then((u) => { if (!stop) { setUser(u); setRetrying(false); setReady(true); } })
        .catch((e) => {
          if (stop) return;
          if (e.status === 401 || !getToken()) { setRetrying(false); setReady(true); return; }
          setRetrying(true);
          tries += 1;
          timer = setTimeout(load, Math.min(15000, 1000 * 2 ** Math.min(tries - 1, 4)));
        });
    };
    load();
    const now = () => { if (!stop && getToken()) { tries = 0; load(); } };
    window.addEventListener('online', now);
    return () => { stop = true; clearTimeout(timer); window.removeEventListener('online', now); };
  }, []);

  useEffect(() => {
    // A member session opened by an admin that ended (idle, revoked) → back to admin.
    const onUnauthorized = () => { if (!restoreAdmin('ended')) setUser(null); };
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, []);

  // Emails go out in the member's saved language. It changes only when the
  // member picks a language with the switch; a language chosen
  // automatically (location, URL) never overwrites it — instead, a
  // signed-in member gets their saved language here.
  useEffect(() => {
    if (!user || !user.lang || user.lang === lang || user.impersonation) return;
    if (langSource === 'saved') {
      api('/auth/lang', { method: 'PATCH', body: { lang } })
        .then(() => setUser((u) => (u ? { ...u, lang } : u)))
        .catch(() => {});
    } else {
      setLang(user.lang);
    }
  }, [user, lang, langSource, setLang]);

  const signIn = useCallback((token, u) => {
    setToken(token);
    setUser(u);
    // The full session (company users: their rights) comes from /me.
    api('/auth/me').then(setUser).catch(() => {});
  }, []);
  const userRef = useRef(null);
  userRef.current = user;
  const signOut = useCallback(() => {
    // Logging out of a member's account opened by an admin ends that
    // session and returns to the admin's own.
    if (userRef.current?.impersonation && store.get(ADMIN_TOKEN_KEY)) { stopImpersonation('logout'); return; }
    store.set(ADMIN_TOKEN_KEY, null); store.set(ADMIN_RETURN_KEY, null);
    setToken(null);
    setUser(null);
  }, []);
  const refresh = useCallback(() => api('/auth/me').then((u) => { setUser(u); return u; }), []);
  // Super admin → the member's account, same tab. The reason is required.
  const startImpersonation = useCallback(async (memberId, reason) => {
    const r = await api(`/admin/impersonation/members/${memberId}`, { method: 'POST', body: { reason } });
    store.set(ADMIN_TOKEN_KEY, getToken());
    store.set(ADMIN_RETURN_KEY, `/admin/members/${memberId}`);
    setToken(r.token);
    setUser(r.user);
    window.location.assign('/account');
  }, []);

  const value = useMemo(() => ({ user, ready, retrying, signIn, signOut, refresh, setUser, startImpersonation }), [user, ready, retrying, signIn, signOut, refresh, startImpersonation]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);

function Loading() {
  const { t } = useI18n();
  const { retrying } = useAuth();
  return <div className="page-loading">{t(retrying ? 'common.reconnecting' : 'common.loading')}</div>;
}

export function RequireAuth({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

export function RequireAdmin({ children }) {
  const { user, ready } = useAuth();
  const location = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  if (user.role !== 'admin') return <Navigate to="/account" replace />;
  return children;
}
